<template>
    <vx-modal v-model:visible="visible" padding="standard" :preventClosing="changingAssignee" size="md">
        <template v-slot:header>Assign To</template>

        <div class="flex flex-col gap-2">
            <vx-alert v-if="error" color="danger" :closeable="false">
                {{ error }}
            </vx-alert>

            <vx-radio-selection v-model="assignTo" aria-label="Assign to">
                <vx-radio-selection-item v-if="hasScope('threads:claim')" value="self" data-test="assign-to-self"
                    >Assign to myself</vx-radio-selection-item
                >
                <vx-radio-selection-item v-if="hasScope('threads:assign')" value="someone" data-test="assign-to-someone"
                    >Assign to someone else</vx-radio-selection-item
                >
            </vx-radio-selection>

            <div v-if="assigneeId !== currentUserId" class="mt-4">
                <vx-label>Assign to</vx-label>
                <vx-selectable
                    v-model="assigneeId"
                    :clearable="false"
                    size="lg"
                    relativeDropdown
                    label="name"
                    :reduce="(option) => option.id"
                    :options="agentsOptions"
                    :disabled="loadingAgents"
                    :placeholder="loadingAgents ? 'Loading agents...' : 'Select agent'"
                >
                </vx-selectable>
            </div>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 justify-between lg:flex-row">
                <vx-button color="muted" size="lg" @click="visible = false"> Cancel </vx-button>

                <vx-button
                    class="grow"
                    @click.prevent="submit()"
                    :loading="changingAssignee"
                    color="primary"
                    data-test="submit-button"
                    size="lg"
                    type="button"
                >
                    Done
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup>
import {
    VxAlert,
    VxButton,
    VxLabel,
    VxModal,
    VxRadioSelection,
    VxRadioSelectionItem,
    VxSelectable,
} from '@voxie/frontend-components';
import { computed, ref, watch } from 'vue';
import { useConversation } from '~/composables/useConversation';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useMessageHub } from '~/components/message-hub/useMessageHub';
import { agentsClient } from '~/services';
import { hasScope } from '~/utils/users';
import { catchRequestError } from '~/utils/request';

const teamId = Spark.state.currentTeam.id;
const currentUserId = Spark.state.user.id;
const { currentOrgUnit } = useCurrentGroup();
const { agents } = useMessageHub();
const { activeThread, assignThread, unassignThread, changingAssignee } = useConversation();

const props = defineProps({
    emitOnly: {
        type: Boolean,
        default: false,
    },
    groupId: {
        type: String,
        default: null,
    },
});

const emit = defineEmits(['submit']);

const visible = defineModel('visible', {
    type: Boolean,
    default: false,
});

const assigneeId = ref(currentUserId);

const assignTo = computed({
    get: () => (assigneeId.value === currentUserId ? 'self' : 'someone'),
    set: (value) => {
        assigneeId.value = value === 'self' ? currentUserId : null;
    },
});

const error = ref('');

const localAgents = ref([]);
const loadingAgents = ref(false);

const loadGroupAgents = async () => {
    if (!currentOrgUnit.value?.id || !props.groupId) {
        return;
    }

    error.value = '';
    loadingAgents.value = true;
    try {
        const response = await agentsClient.getAgents({ teamId, groupId: props.groupId });
        localAgents.value = response.data;
    } catch (e) {
        error.value = catchRequestError(e);
        localAgents.value = [];
    } finally {
        loadingAgents.value = false;
    }
};

const agentsOptions = computed(() => {
    const agentsList = currentOrgUnit.value?.id ? localAgents.value : agents.value;
    return [
        {
            id: 'unassigned',
            name: 'None (Unassigned)',
        },
        ...agentsList.filter((a) => a.id !== Spark.state.user.id),
    ];
});

const submit = async () => {
    if (!assigneeId.value) {
        error.value = 'Please select an agent.';
        return;
    }

    if (props.emitOnly) {
        emit('submit', assigneeId.value);
        visible.value = false;
        return;
    }

    if (!activeThread.value.contact_id) {
        error.value = 'Please select a conversation first.';
        return;
    }

    const request =
        assigneeId.value === 'unassigned'
            ? unassignThread(activeThread.value)
            : assignThread(activeThread.value, assigneeId.value);

    await request;
    visible.value = false;
};

watch(
    visible,
    (visible) => {
        if (!visible) {
            return;
        }

        if (currentOrgUnit.value?.id && props.groupId) {
            loadGroupAgents();
        }

        if (props.emitOnly) {
            assigneeId.value = currentUserId;
        } else {
            assigneeId.value = activeThread.value.assignee_id || currentUserId;
        }
    },
    {
        immediate: true,
    }
);
</script>
